/**
 * useReportEscalation - Escalation and reminder state for an admin report ticket
 *
 * Extracted from useAdminReportData.js. Owns:
 * - Escalation level/timing state and reminder windows
 * - Manual escalation to higher-tier admins
 * - Muting/unmuting reminders (top-tier admin only)
 * - clearEscalationState, shared with sibling hooks whose actions restart
 *   the escalation clock (status changes, comments, todos, attachments)
 *
 * @param {string} uuid - The report UUID
 * @param {number} timestamp - The report timestamp (sort key)
 */

import { useState, useCallback } from 'react';

import Util from '../util/util';
import { showSuccess, showToast } from '../util/error/hookErrors';
import { t } from '../i18n';

const useReportEscalation = (uuid, timestamp) => {
  const [escalationLevel, setEscalationLevel] = useState(0);
  const [escalatedAt, setEscalatedAt] = useState(null);
  const [nextEscalationAt, setNextEscalationAt] = useState(null);
  const [escalationWindowMs, setEscalationWindowMs] = useState(null);
  const [nextReminderAt, setNextReminderAt] = useState(null);
  const [reminderWindowMs, setReminderWindowMs] = useState(null);
  const [remindersMuted, setRemindersMuted] = useState(false);
  const [remindersCapped, setRemindersCapped] = useState(false);

  /** Reset escalation UI state — call after any action that restarts the escalation clock */
  const clearEscalationState = useCallback(() => {
    setEscalationLevel(0);
    setEscalatedAt(null);
    setNextEscalationAt(null);
    setEscalationWindowMs(null);
    setNextReminderAt(null);
    setReminderWindowMs(null);
    setRemindersCapped(false);
  }, []);

  /** Populate escalation state from a fetched report */
  const initializeEscalation = useCallback((report) => {
    setEscalationLevel(report.escalationLevel || 0);
    setEscalatedAt(report.escalatedAt || null);
    setNextEscalationAt(report.nextEscalationAt || null);
    setEscalationWindowMs(report.escalationWindowMs || null);
    setNextReminderAt(report.nextReminderAt || null);
    setReminderWindowMs(report.reminderWindowMs || null);
    setRemindersMuted(report.remindersMuted === true);
    setRemindersCapped(report.remindersCapped === true);
  }, []);

  /**
   * Manually escalate the ticket to higher-tier admins
   */
  const escalateTicket = useCallback(
    async () => {
      if (escalationLevel >= 2) {
        return;
      }

      const requestData = {
        queryParams: {
          uuid: uuid,
          timestamp: timestamp,
        },
      };

      try {
        const response = await Util.patch('Resty', '/items/escalate', requestData);
        const newLevel = response?.escalationLevel || escalationLevel + 1;
        setEscalationLevel(newLevel);
        setEscalatedAt(Date.now());
        if (response?.warning) {
          // Level was bumped but no email was sent — surface the reason so
          // the admin knows to configure tier routing instead of assuming
          // "escalated" means "someone was notified". The warning string
          // from the server already includes the new level.
          showToast(response.warning, 'warning');
        } else {
          showSuccess(t('reports:ticket.escalatedToLevel', { level: newLevel }));
        }
        await Util.setReportCache(uuid, 'reportChanged', true);
      } catch (err) {
        Util.handleApiError(err, { userMessage: t('reports:ticket.escalateFailed') });
      }
    },
    [uuid, timestamp, escalationLevel]
  );

  /**
   * Mute or unmute reminders/escalations for this ticket (top-tier admin only)
   */
  const toggleRemindersMuted = useCallback(
    async () => {
      const newMuted = !remindersMuted;
      const requestData = {
        queryParams: { uuid, timestamp },
        body: { muted: newMuted },
      };

      try {
        await Util.patch('Resty', '/items/reminders-muted', requestData);
        setRemindersMuted(newMuted);
        if (newMuted) {
          setNextEscalationAt(null);
          setNextReminderAt(null);
        }
        showSuccess(t(newMuted ? 'reports:ticket.remindersMuted' : 'reports:ticket.remindersResumed'));
        await Util.setReportCache(uuid, 'reportChanged', true);
      } catch (err) {
        Util.handleApiError(err, { userMessage: t('reports:ticket.reminderUpdateFailed') });
      }
    },
    [uuid, timestamp, remindersMuted]
  );

  return {
    escalationLevel,
    escalatedAt,
    nextEscalationAt,
    escalationWindowMs,
    nextReminderAt,
    reminderWindowMs,
    remindersMuted,
    remindersCapped,
    clearEscalationState,
    initializeEscalation,
    escalateTicket,
    toggleRemindersMuted,
  };
};

export default useReportEscalation;
